
      import { computed, defineComponent, onMounted, PropType, reactive, ref, toRefs, watch } from 'vue'

      import { PRICING_SERVICES_ACCOUNT_CONFIG, PRICING_SERVICES } from '@/constants'
      import { ServiceGroup, ServiceType } from '@/models/interfaces/service'
      import { useStore } from 'vuex'
      import Directr, { ContentType } from '@/services/directr'

      import { createConfig } from './constants/serviceFields'
      import { useQuasar } from 'quasar'
      import { ulid } from 'ulid'

      import { CtShipmentCarrier } from '@/models/interfaces/content-type'
      import { format } from '@/utils/template'

      import Sys from '@/services/sys'
      import { Configuration, ServiceConfigurationDict, ServiceInfo } from '@/pages/Settings/constants'

      import DForm from '@/components/base/DForm.vue'
      import { errorToReadableString } from '@/utils/error'
      import { debounce } from '@/utils'
    import { Tenant } from '@/pages/Settings/components/editors/TenantConfigEditor.vue'

      interface PricingServicesState {
        services: Array<ServiceInfo & { draft?: boolean }>,
        accounts: Record<keyof ServiceConfigurationDict, Array<Configuration>>,
        expanded: Array<string>
        active: string | null
      }

      // NOTE: backend configurations object should provide this info, but we will put it here for now
      const PRICING_SERVICE_AUTH_TYPE = {
        'syscalc': {default: 'apikey'},
      }

      export default defineComponent({
        name: 'PricingServices',
        components: { DForm },
        emits: [],
        props: {
          tenant: {
            type: Object as PropType<Tenant>
          }
        },
        setup (props) {
          const $q = useQuasar()
          const $store = useStore()

          const $sys: Sys = $store.getters['Services/Sys']

          const state = reactive<PricingServicesState>({
            services: [],
            accounts: {},
            expanded: [],
            active: null
          })

          const editingAuth = ref<string>()
          const revealingAuth = ref<boolean>()

          watch(() => state.active, () => editingAuth.value = '')

          // aggregate of all unique service types used by current carriers
          const pricingServicesByType = computed(() => {
            return (state.services || []).reduce((dict, service) => {

              if (dict[service.type]) dict[service.type].push({ service, accounts: state.accounts[service.id] })
              else dict[service.type] = [{ service, accounts: state.accounts[service.id] }]

              return dict
            }, {})
          })

          async function load () {

            const serviceIds = await $sys.get('service').then(data => data.filter(d => d.group === ServiceGroup.Pricing).map(d => d.id))

            const services = await $sys.configure.get(serviceIds)

            services.forEach((service) => {
              service.items.forEach((item) => {
                state.services.push(item.service)
                state.accounts[item.service.id] = (item.configurations.accounts?.items || []).map(a => ({ ...a, meta: a.meta || {} }))
              })
            })
          }

          onMounted(() => {
            void load()
          })

          function createProvider () {
            // const carrierService = await $directr.put('carrier', { name: carrierName }).then(carrier => carrier.items)
            // await createCarrierService(selectedCarrier, serviceId, carrierName)
            // const updated = await $directr.put('carrier', carrierService?.[0].sys.id, { service: serviceId }).then(carrier => carrier.items?.[0] as CtShipmentCarrier)

            // createProviderAccount()
          }


          function setProviderAccountMeta (serviceId: string, serviceType: ServiceType, accountId, accountMeta: Record<string, any>) {
            // console.debug('setProviderAccountMeta', serviceId, serviceType, accountId, accountMeta)
            const accountIndex = state.accounts[serviceId].findIndex(a => a.id === accountId)
            if (accountIndex >= 0) {
              const updated = { ...state.accounts[serviceId][accountIndex], meta: accountMeta }
              state.accounts[serviceId].splice(accountIndex, 1, updated)
            }
          }

          async function saveProviderAccount (serviceId: string, serviceType: ServiceType, account: Configuration) {
            if (account.draft) {
              const created = await createProviderAccount(serviceId, serviceType, account)
              console.log(serviceId, serviceType, account.id, 'account created', created)
              return
            }

            // patch service with new account details (i.e keystore entry)
            const updated = await $sys.configure.update(serviceId, account)
            console.log(serviceId, serviceType, account.id, 'account updated', updated)
            $q.notify({ message: 'Shipment service updated', type: 'positive', classes: 'custom-notify success' })
          }

          async function createProviderAccount (serviceId: string, serviceType: ServiceType, account: Record<string, any>) {
            const service = state.services.find(s => s.id === serviceId)
            if (!service) return

            const carrierServiceId = serviceId

            // check if service exists (i.e is draft)
            if (service.draft) {

              const notifier = $q.notify({ message: 'Creating pricing service...', classes: 'custom-notify', group: false, spinner: true })


              notifier({ message: 'Registering service...' })

              const createdService = await $sys.services.create(
                serviceType,
                carrierServiceId,
                {
                  name: service.name,
                  active: true,
                  // NOTE config will immediately be converted to account, though any set meta will initially be lost so it should be patched to the account during verifiction
                  config: createConfig(PRICING_SERVICE_AUTH_TYPE[service.type][account.type || 'default'], account.token?.access_token || account.basic_auth?.pass, account.basic_auth?.user)
                }
              )

              delete service.draft

              notifier({ message: 'Pricing service created', type: 'positive', classes: 'custom-notify success', spinner: false })
            }

            // patch service with new account details (i.e keystore entry)
            delete account.draft
            const updated = await $sys.configure.update(carrierServiceId, account)

            return updated
          }

          function draftProviderAccount (serviceId: string, serviceType: ServiceType, accountType?: string) {
            // add (additional) account to an existing provider
            const id = ulid()
            if (!state.accounts[serviceId]) state.accounts[serviceId] = [{ id, draft: true, type: accountType || PRICING_SERVICES_ACCOUNT_CONFIG[serviceType].default.type, meta: { ...PRICING_SERVICES_ACCOUNT_CONFIG[serviceType][accountType || 'default'] } }]
            else state.accounts[serviceId].push({ id, draft: true, type: accountType || PRICING_SERVICES_ACCOUNT_CONFIG[serviceType].default.type, meta: { ...PRICING_SERVICES_ACCOUNT_CONFIG[serviceType][accountType || 'default'] } })
            toggleExpandItem(id)
          }

          function deleteProviderAccount (serviceId: string, accountId: string, force: boolean) {
            const index = state.accounts[serviceId]?.findIndex(a => a.id === accountId)
            if (index === undefined || index < 0) return

            const onOk = () => {
              if (force) return state.accounts[serviceId].splice(index, 1)

              // call service config delete endpoint & splice on success
              $sys.configure.delete(serviceId, accountId).then(() => {
                state.accounts[serviceId].splice(index, 1)
                $q.notify({ message: 'Shipment service removed', type: 'positive', classes: 'custom-notify success' })
              }).catch(console.error)
            }

            if (force === true) return onOk()

            $q.dialog({
              cancel: true,
              title: 'Delete account?',
              message: 'Are you sure you want to delete this account? This action cannot be undone',
              persistent: true
            })
              .onCancel(() => {
                //
              })
              .onDismiss(() => {
                //
              })
              .onOk(onOk)

          }

          function draftProvider (serviceType: ServiceType) {
            // push draft item to list with draft account item, which will display inputs based on the service type from the instructions dict
            const id = ulid()
            const accountId = ulid()
            state.services.push({ id, draft: true, name: `New ${serviceType} service`, type: serviceType, group: ServiceGroup.Pricing})
            // state.carriers.push({ draft: true, name: `New ${serviceType} service`, service: id })
            state.accounts[id] = [{ id: accountId, draft: true, meta: { ...PRICING_SERVICES_ACCOUNT_CONFIG[serviceType].default } }]
            state.active = serviceType
            toggleExpandItem(accountId)
          }

          function setActive (serviceType: ServiceType) {
            state.active = serviceType
          }

          function setAccountCode (serviceId: string, accountId: string, code: string) {
            const accountIndex = state.accounts[serviceId]?.findIndex(a => a.id === accountId)
            if (accountIndex === undefined || accountIndex < 0) return
            const updated = { ...state.accounts[serviceId][accountIndex]}
            if (!updated.token) updated.token = { code }
            else updated.token.access_token = code
            state.accounts[serviceId].splice(accountIndex, 1, updated)
          }

          function setAccountToken (serviceId: string, accountId: string, access_token: string) {
            const accountIndex = state.accounts[serviceId]?.findIndex(a => a.id === accountId)
            if (accountIndex === undefined || accountIndex < 0) return
            const updated = { ...state.accounts[serviceId][accountIndex]}
            if (!updated.token) updated.token = { access_token }
            else updated.token.access_token = access_token

            state.accounts[serviceId].splice(accountIndex, 1, updated)
          }

          function setAccountBasicAuth (serviceId: string, accountId: string, field: 'user' | 'pass', value: string) {
            if (!field) return

            const accountIndex = state.accounts[serviceId]?.findIndex(a => a.id === accountId)
            if (accountIndex === undefined || accountIndex < 0) return

            const basic_auth = state.accounts[serviceId][accountIndex].basic_auth || { user: '', pass: '' }
            basic_auth[field] = value

            const updated = { ...state.accounts[serviceId][accountIndex], basic_auth }
            state.accounts[serviceId].splice(accountIndex, 1, updated)
          }


          async function updatePricingServiceName (serviceId: string, name: string) {
            const service  =  state.services.find(s => s.id === serviceId)
            // console.log('updatePricingServiceName', serviceId, name, service)
            if (service?.draft || !service) return

            await $sys.services.patch(service.type, service.id, { name: name })

            $q.notify({ message: 'Pricing service name updated', type: 'positive', classes: 'custom-notify success' })
          }

          function toggleExpandItem (id) {
            const index = state.expanded.indexOf(id)
            if (index >= 0) state.expanded.splice(index, 1)
            else state.expanded.push(id)
          }

          async function authVerifier (serviceId: string, account: Configuration, type: string) {
            const notifier = $q.notify({ message: 'Verifying...', group: false, spinner: true })
            try {
              // if a type is given, call checkServiceAuth via the service manager
              if (type) {
                const verification = await $sys.post(`/service/ctrl/auth.verify`, [account.id], { id: serviceId, type, config: { apiKey: account.apiKey, basicAuth: account.basic_auth, oauth: { client_id: account.token?.code, client_secret: account.token?.access_token } } }, {})
              } else {
                // make a request to the parent service using the account credentials
                const verification = await $sys.post(`/service/${serviceId}/auth.verify`, [account.id], { account })
              }

              notifier({ message: 'Authentication credentials verified', type: 'positive', classes: 'custom-notify success', spinner: false })
            } catch (e) {
              notifier({ message: 'Verification failed', caption: errorToReadableString(e), type: 'negative', classes: 'custom-notify error', spinner: false })
            }
          }

          const verifyAuth = debounce(authVerifier, 500)

          function editAuthWarn (account: Configuration) {
            if (account.draft) return editingAuth.value = account.id

            $q.dialog({
              cancel: true,
              ok: 'Edit key',
              title: 'Edit Authentication Key?',
              message: 'This key authenticates shipment requests for this account. Are you sure you want to change it? Only do this if you are sure you are updating it with a valid API key',
              persistent: true
            })
              .onCancel(() => {
                //
              })
              .onDismiss(() => {
                //
              })
              .onOk(() => editingAuth.value = account.id)
          }

          return {
            ...toRefs(state),
            verifyAuth,
            editAuthWarn,
            format,
            pricingServicesByType,
            PRICING_SERVICES: computed(() => {
              return PRICING_SERVICES.filter(s => !s.private || (props.tenant?.settings?.private_services?.includes(s.value)))
            }),
            PRICING_SERVICES_ACCOUNT_CONFIG,
            PRICING_SERVICE_AUTH_TYPE,
            draftProvider,
            createProvider,
            setProviderAccountMeta,
            saveProviderAccount,
            setAccountToken,
            setAccountCode,
            setAccountBasicAuth,
            updatePricingServiceName,
            // createProviderAccount,
            draftProviderAccount,
            deleteProviderAccount,
            setActive,
            toggleExpandItem,
            editingAuth,
            revealingAuth
          }
        }
      })
    